<template>
  <div>
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト管理</span>
          <span class="card-header-actions">
            <router-link :to="{ name: projectRegister }">
              <button class="btn btn-primary">新規登録</button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info">
          プロジェクトの登録・更新・削除と、自分自身の所属プロジェクトの追加・解除ができます。
          他の社員の所属変更、通知設定、単価設定は管理権限を持つ担当者が行います。
        </CAlert>

        <CRow v-if="errors.length">
          <ul>
            <li v-for="error in errors" :key="error" class="preika-form-message">
              {{ error }}
            </li>
          </ul>
        </CRow>

        <div v-if="myProjects.length" class="mb-3">
          <h5 class="mb-2">自分の所属プロジェクト</h5>
          <CRow class="mb-3">
            <CCol
              v-for="projectItem in myProjects"
              :key="projectItem.id"
              sm="12"
              md="6"
              lg="4"
              xl="3"
            >
              <CCard
                color="info"
                class="text-white card-select"
                @click="openProject(projectItem.id)"
              >
                <CCardBody class="py-3">
                  <div class="font-weight-bold">{{ projectItem.name }}</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th class="text-center">自分の所属</th>
                <th scope="col">プロジェクト名</th>
                <th scope="col">顧客会社名</th>
                <th scope="col">顧客会社担当者</th>
                <th scope="col">自社管理者</th>
                <th scope="col">契約形態</th>
                <th scope="col">基準時間</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in projects" :key="item.id">
                <td class="text-center">
                  <label class="custom-switch mb-0">
                    <input
                      type="checkbox"
                      class="custom-switch-input"
                      :id="`self-prj-${item.id}`"
                      :checked="isAssigned(item.id)"
                      :disabled="membershipSaving || deleteLoading"
                      @change="onToggle(item, $event.target.checked)"
                    />
                    <span class="custom-switch-indicator"></span>
                  </label>
                </td>
                <td>
                  <router-link :to="projectInfoRoute(item)">
                    {{ item.name }}
                  </router-link>
                </td>
                <td>{{ item.company_name }}</td>
                <td>{{ item.staff_name }}</td>
                <td>{{ item.private_staff_name }}</td>
                <td>{{ item.contract }}</td>
                <td>{{ item.base_time }}</td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-danger"
                    :disabled="membershipSaving || deleteLoading"
                    @click="openDeleteModal(item)"
                  >
                    削除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ProjectDeleteModal
          v-if="showDeleteModal"
          :draggable="true"
          :resizable="true"
          :val="deleteItem"
          :loading="deleteLoading"
          @delete="deleteProject"
          @close="closeDeleteModal"
        />

        <div class="d-flex justify-content-center">
          <VPagination
            v-model="currentPage"
            :click-handler="changePage"
            :page-count="totalPages"
            :force-page="currentPage"
            :force-ellipses="true"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-text="'<'"
            :next-text="'>'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :page-link-class="'page-link'"
            :class-prefix="'pagination'"
          />
        </div>
      </CCardBody>
    </CCard>

    <div
      v-if="loading || membershipSaving || deleteLoading"
      class="loading-overlay"
      aria-live="polite"
      aria-busy="true"
    >
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VPagination from 'vuejs-paginate'
import ConstList from './../ConstList'
import ProjectDeleteModal from './ProjectDeleteModal'
import { recordQueryRoute } from '@/common/routeRecordId'

/** PROJECT_EDIT保持者向け。旧commonのプロジェクトCRUDと本人所属を提供する。 */
export default {
  name: 'ProjectEditorList',
  components: {
    ProjectDeleteModal,
    VPagination
  },
  data () {
    return {
      projects: [],
      myProjects: [],
      errors: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      projectRegister: ConstList.ROUTE_PROJECT_REGISTER,
      projectInfo: ConstList.ROUTE_PROJECT_INFO,
      loading: false,
      membershipSaving: false,
      deleteLoading: false,
      showDeleteModal: false,
      deleteItem: null
    }
  },
  mounted () {
    this.loadPage()
  },
  methods: {
    async loadPage () {
      this.loading = true
      this.errors = []
      try {
        const [listResponse, countResponse, assignedResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/project`, {
            params: {
              currentPage: this.currentPage,
              pageSize: this.pageSize
            }
          }),
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/projectCount`),
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/projects`)
        ])

        this.projects = Array.isArray(listResponse.data) ? listResponse.data : []
        this.myProjects = Array.isArray(assignedResponse.data) ? assignedResponse.data : []
        const totalCount = Number(countResponse.data || 0)
        this.totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize))
        if (!this.projects.length) this.errors.push(ConstList.EMPTY)
      } catch (error) {
        this.errors = ['プロジェクト情報を取得できませんでした。']
        this.reportError(error)
      } finally {
        this.loading = false
      }
    },

    isAssigned (projectId) {
      return this.myProjects.some(item => Number(item.id) === Number(projectId))
    },

    async onToggle (item, nowChecked) {
      const previousProjects = this.myProjects.slice()
      this.membershipSaving = true
      try {
        const endpoint = nowChecked ? '/userProject/add' : '/userProject/remove'
        await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}${endpoint}`,
          { projectId: item.id },
          { headers: { 'Content-Type': 'application/json' } }
        )

        if (nowChecked) {
          if (!this.isAssigned(item.id)) {
            this.myProjects = [{ id: item.id, name: item.name }, ...this.myProjects]
          }
        } else {
          this.myProjects = this.myProjects.filter(
            project => Number(project.id) !== Number(item.id)
          )
        }

        try {
          await this.refreshAssignments()
        } catch (refreshError) {
          this.errors = [
            '所属は保存されましたが、確認結果を再取得できませんでした。画面を再読み込みしてください。'
          ]
          this.reportError(refreshError)
        }
      } catch (error) {
        this.myProjects = previousProjects
        this.errors = ['所属プロジェクトを更新できませんでした。画面を再読み込みしてください。']
        this.reportError(error)
      } finally {
        this.membershipSaving = false
      }
    },

    async refreshAssignments () {
      const response = await axios.get(
        `${process.env.VUE_APP_API_ORIGIN}/projects`
      )
      this.myProjects = Array.isArray(response.data) ? response.data : []
    },

    openDeleteModal (item) {
      this.deleteItem = item
      this.showDeleteModal = true
    },

    closeDeleteModal () {
      this.showDeleteModal = false
      this.deleteItem = null
    },

    async deleteProject () {
      if (!this.deleteItem || !this.deleteItem.id) return

      const target = this.deleteItem
      this.deleteLoading = true
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}/projectDelete`,
          { id: target.id },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const affiliationCount = Number(response.data || 0)
        if (affiliationCount > 0) {
          this.errors = [
            `プロジェクトを設定中の方が${affiliationCount}人います。所属を解除してから削除してください。`
          ]
          return
        }

        this.projects = this.projects.filter(
          project => Number(project.id) !== Number(target.id)
        )
        this.myProjects = this.myProjects.filter(
          project => Number(project.id) !== Number(target.id)
        )
        this.closeDeleteModal()
        alert(ConstList.DELETE_MESSAGE)

        try {
          const countResponse = await axios.get(
            `${process.env.VUE_APP_API_ORIGIN}/projectCount`
          )
          const totalCount = Number(countResponse.data || 0)
          this.totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize))
          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages
            await this.loadPage()
          }
        } catch (refreshError) {
          this.errors = [
            'プロジェクトは削除されましたが、件数を再取得できませんでした。画面を再読み込みしてください。'
          ]
          this.reportError(refreshError)
        }
      } catch (error) {
        this.errors = ['プロジェクトを削除できませんでした。画面を再読み込みしてください。']
        this.reportError(error)
      } finally {
        this.deleteLoading = false
      }
    },

    projectInfoRoute (item) {
      return {
        ...recordQueryRoute(this.projectInfo, item.id),
        params: { value: item }
      }
    },

    openProject (id) {
      const row = this.projects.find(item => Number(item.id) === Number(id))
      if (row) {
        this.$router.push(this.projectInfoRoute(row)).catch(() => {})
        return
      }
      this.$router.push(recordQueryRoute(this.projectInfo, id)).catch(() => {})
    },

    changePage (page) {
      this.currentPage = page
      this.loadPage()
    },

    reportError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
th,
td {
  white-space: nowrap;
}

td {
  vertical-align: middle;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
